import { IViolation } from 'app/model/base/annotation';
import { arrayIsNullOrEmpty } from 'app/registries/utls/util';
import { ValueAdapter } from 'app/model/adapters/value.adapter';
import { DeltaDataset, FormatDatasetV2, StoreSettingMetadata, StoreSettingPartitionKey } from 'shell-models-lib';
import { BinarySink } from 'app/model/pipeline/copysink/binary.sink';
import { isString } from 'app/shared/utils/string.util';
import { CopyActivity } from 'app/model/pipeline/copy.activity';
import { AdvancedStoreWriteSettings } from 'app/model/pipeline/copysink/store-write-settings/advanced-store-write-settings';
import { LakehouseWriteSettings } from 'app/model/pipeline/copysink/store-write-settings/lakehouse-store-write-settings';

export async function validateMetadataContent(storeSettings: AdvancedStoreWriteSettings): Promise<IViolation[] | undefined> {
    if (!arrayIsNullOrEmpty(storeSettings.metadata)) {
        const nameSet = new Set<string>();
        for (const metadatum of storeSettings.metadata) {
            if (!metadatum.name) {
                return [{
                    context: storeSettings.metadata,
                    message: () => stringResources.Metadata_Not_Allow_EmptyName
                }];
            }
            if (nameSet.has(metadatum.name)) {
                return [{
                    context: storeSettings.metadata,
                    message: () => stringResources.Metadata_Not_Allow_DuplicateName
                }];
            }

            const value = ValueAdapter.toResource(metadatum, StoreSettingMetadata.VALUE);
            if (!value) {
                return [{
                    context: storeSettings.metadata,
                    message: () => stringResources.Metadata_Not_Allow_EmptyValue
                }];
            }
            // in cw, storeSettings.owner is null
            if (isString(value) && value.indexOf('$$') === 0 && !(value === '$$LASTMODIFIED' && (!storeSettings.owner || storeSettings.owner instanceof BinarySink))) {
                // $$LASTMODIFIED is predefined value that BE accepts
                // $$CUSTOM does not apply here as value would be the actual string user defined
                return [{
                    context: storeSettings.metadata,
                    message: () => stringResources.Metadata_Invalid_Value
                }];
            }
            nameSet.add(metadatum.name);
        }
    }
    return undefined;
}

export async function validatePartitionNames(storeSettings: LakehouseWriteSettings): Promise<IViolation[] | undefined> {
    const sinkDataset = (storeSettings.owner?.owner as CopyActivity)?.sinkDataset as FormatDatasetV2;
    if (!sinkDataset || !(sinkDataset instanceof DeltaDataset) || sinkDataset.rootFolder !== 'Tables' || storeSettings.tableActionOption !== 'Overwrite') {
        return undefined;
    }
    const nameSet = new Set<string>();
    if (storeSettings.enablePartitionOption && Array.isArray(storeSettings.partitionNameList)) {
        if (arrayIsNullOrEmpty(storeSettings.partitionNameList)) {
            return [{
                context: storeSettings.partitionNameList,
                message: () => stringResources.Partition_Name_Not_Allow_EmptyName
            }];
        }
        for (const partitionName of storeSettings.partitionNameList) {
            const templated = ValueAdapter.isTemplated(partitionName, StoreSettingPartitionKey.PARTITITON_KEY);
            const value = templated ? ValueAdapter.toResource(partitionName, StoreSettingPartitionKey.PARTITITON_KEY).value : partitionName.partitionKey;
            if (!value) {
                return [{
                    context: storeSettings.partitionNameList,
                    message: () => stringResources.Partition_Name_Not_Allow_EmptyName
                }];
            }
            if (nameSet.has(value)) {
                return [{
                    context: storeSettings.partitionNameList,
                    message: () => stringResources.Partition_Name_Not_Allow_DuplicateName
                }];
            }
            nameSet.add(value);
        }
    }
    const copyActivity = storeSettings.owner.owner as CopyActivity;

    if (nameSet.size > 0 && nameSet.size === copyActivity.cacheSourceSchema?.length) {
        return [{
            context: storeSettings.partitionNameList,
            message: () => stringResources.Partition_Names_Not_Allow_All_Selected
        }];
    }
    return undefined;
}

